import { useQuery, UseQueryOptions, UseQueryResult } from "react-query";
import { endpoints } from "../api/constants/endpoints";
import fetcher from "../utils/fetchWrapper";

export interface GetConfigResponse {
  data: {
    config: {
      enabled_features: EnabledFeatures;
    };
  };
  duration: number;
  messages: any[];
}
export interface EnabledFeatures {
  ACH_PAYMENT: {
    enabled: boolean;
  };
  BILLING: {
    enabled: boolean;
  };
  CREATE_ACCOUNT: {
    enabled: boolean;
  };
  MANAGED_BILLING: {
    enabled: boolean;
  };
  PAUSE_SERVICE: {
    enabled: boolean;
  };
  USER_REVIEWS: {
    enabled: boolean;
  };
  VBG_INSURANCE: {
    enabled: boolean;
  };
}

const getConfig = async () => {
  const ONE_DAY_MS = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  // First, try to get the data from localStorage
  try {
    const cachedString = localStorage.getItem("enabledfeatures");
    if (cachedString) {
      const cached = JSON.parse(cachedString);

      // Check if the cache is fresh (less than 24 hours old)
      const timestamp = localStorage.getItem("enabledfeatures_timestamp");
      if (timestamp && Date.now() - Number(timestamp) < ONE_DAY_MS) {
        return cached;
      }
      // If we get here, the cache is stale, but we'll still return it while refreshing
      // This prevents waiting on the API call

      // Refresh the cache in the background
      fetcher(endpoints.getEnabledFeatures)
        .then((res) => res.json())
        .then((freshData) => {
          localStorage.setItem("enabledfeatures", JSON.stringify(freshData));
          localStorage.setItem(
            "enabledfeatures_timestamp",
            Date.now().toString()
          );
        })
        .catch((err) => console.log("Background refresh failed:", err));

      return cached;
    }
  } catch (error) {
    console.log("Error reading from localStorage, will fetch from API");
  }

  // If not available in localStorage or handling the error, fetch from API
  const response = await fetcher(endpoints.getEnabledFeatures);

  if (!response.ok) {
    throw new Error();
  }

  const data = await response.json();

  // Store in localStorage with timestamp for future use
  try {
    localStorage.setItem("enabledfeatures", JSON.stringify(data));
    localStorage.setItem("enabledfeatures_timestamp", Date.now().toString());
  } catch (error) {
    console.log("Error writing to localStorage");
  }

  return data;
};

export const useGetConfig = (
  options?: UseQueryOptions<any, Error, any, Array<string>>
): UseQueryResult<GetConfigResponse, Error> =>
  useQuery(["config"], getConfig, {
    staleTime: 24 * 60 * 60 * 1000, // 24 hours - expire daily
    cacheTime: 24 * 60 * 60 * 1000, // 24 hours - expire daily
    refetchOnMount: false, // No refetch on mount to reduce unnecessary calls
    refetchOnWindowFocus: false, // No refetch on window focus
    ...options,
  });
